import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  LoadingPage, BASE_UI_URL, apiExploreCountries, apiGetProfileInfo,
  NotificationBanner, apiSaveWishList, apiGetStamps, DEFAULT_MAX_RETRY,
  ImagePrefetcher,
} from '@americanexpress/arcade-core';
import { fromJS } from 'immutable';
import { useReactlytics } from '@americanexpress/reactlytics';
import { MODULE_NAME, STAMPS_SCAN_STATUS } from '../constants';
import styles from './styles/renderArcadeChildren.scss';
import {
  apiProfileRefresh,
  apiSurveyPromptStatus,
  apiSubmitSurveyPrompt,
  apiPollStampsScanStatus,
} from '../api';
import NavBar from './NavBar';
import SurveyPopUp from './SurveyPopUp';

const renderLink = ({ chunks, onClick }) => (
  <button
    className={`btn btn-tertiary pad-0 text-underline ${styles.link}`}
    type="button"
    onClick={onClick}
  >
    {chunks}
  </button>
);

const RenderArcadeChildren = ({
  isMobile,
  isNative,
  children,
  router,
  showNavbar,
  pathname,
}) => {
  const isStampDetailsCalled = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'isStampDetailsCalled']));
  const userStatus = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'userStatus']));
  const isFullScreenError = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'isFullScreenError']));
  const isWishlistError = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'wishlistError', 'isError']));
  const isStampsListApiError = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'isStampsListApiError']));
  const sourcePage = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'wishlistError', 'unsavedPayload', 'sourcePage']));
  const wishlistCountryCodes = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'wishlistError', 'unsavedPayload', 'wishlistCountryCodes']));
  const previouslyWishlistedCountryCodes = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'wishlistError', 'unsavedPayload', 'previouslyWishlistedCountryCodes']));
  const canProfileRefresh = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'canProfileRefresh']));
  const showScanBanner = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'showScanBanner']));
  const stampsScanStatus = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'stampsScanStatus']));
  const isUserUnenrolled = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'isUserUnenrolled']));
  const isEnrollmentCapMet = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'isEnrollmentCapMet']));
  const fetchedUrlModuleNames = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'fetchedUrlModuleNames']))?.toArray() || [];
  const isCountryListLoading = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'isCountryListLoading']));
  const isCountryListApiError = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'isCountryListApiError']));
  const isWishlistSaving = useSelector((state) => state.getIn(['modules', 'arcade-root', 'common', 'isWishlistSaving']));
  const gemUrl = useSelector((state) => state.getIn(['config', 'GemUrl']));
  const dispatch = useDispatch();
  const { track } = useReactlytics();
  const { search, query, hash } = router.location;
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(true);
  const [isSurveyPopUpOpen, setIsSurveyPopUpOpen] = useState(false);
  const [surveyPromptStatus, setSurveyPromptStatus] = useState(false);
  const [wishlistRetryCount, setWishlistRetryCount] = useState(0);
  const [stampsRetryCount, setStampsRetryCount] = useState(0);
  const [shouldPrefetch, setShouldPrefetch] = useState(false);
  const [isCountryListApiInitiated, setCountryListApiInitiated] = useState(false);
  const [isWishlistSaveInitiated, setWishlistSaveInitiated] = useState(false);
  const intl = useIntl();

  useEffect(() => {
    const prevUserStatus = localStorage.getItem('prevUserStatus');
    if (userStatus === 'enrolled') {
      setIsFirstTimeUser(prevUserStatus ? JSON.parse(prevUserStatus)?.isFirstTimeUser : true);
    } else {
      setIsFirstTimeUser(true);
    }

    const handleRouteToLanding = () => {
      if (query?.faq === 'true') {
        router.replace(`${BASE_UI_URL}/landing/#faq`);
      } else if (hash !== '#faq') {
        /* checking #faq as this function was
        running twice and hence removing the #faq */
        router.replace(`${BASE_UI_URL}/landing`);
      }
    };

    if (userStatus && isMobile) {
      switch (userStatus) {
        case 'unenrolled':
        case 'not_enrolled':
          dispatch(apiGetProfileInfo({
            collectionMethod: 'apiGetProfileInfo - Exception',
            moduleID: 'arcade-page-wrapper',
          }));
          if (pathname.includes('/enroll') && !isEnrollmentCapMet) {
            const targetSlide = `${BASE_UI_URL}/enroll${search}`;
            router.replace(targetSlide);
          } else if (isNative || isUserUnenrolled || isEnrollmentCapMet) {
            handleRouteToLanding();
          } else {
            router.replace(`${BASE_UI_URL}/enroll`);
          }
          break;
        case 'enrolled':
          dispatch(apiExploreCountries({
            collectionMethod: 'apiExploreCountries - Exception',
            moduleID: 'arcade-page-wrapper',
          }));
          setCountryListApiInitiated(true);
          dispatch(apiGetStamps({
            collectionMethod: 'apiGetStamps - Exception',
            moduleID: 'arcade-page-wrapper',
          }));
          if (query?.faq === 'true') {
            router.replace(`${BASE_UI_URL}/landing/#faq`);
          }
          break;
        case 'ineligible':
          router.replace(`${BASE_UI_URL}/landing`);
          break;
        case 'unauthorized':
          handleRouteToLanding();
          break;
        /* istanbul ignore next -- default case */
        default:
          // router.push('/');
          break;
      }
    }

    if (!isMobile) {
      handleRouteToLanding();
    }
  }, [userStatus, isMobile]);

  useEffect(() => {
    if (isUserUnenrolled) {
      router.replace(`${BASE_UI_URL}`);
    }
  }, [isUserUnenrolled]);

  useEffect(() => {
    if (!isCountryListLoading && !isCountryListApiError && isCountryListApiInitiated) {
      const unsavedWishlist = JSON.parse(localStorage.getItem('unsavedWishlist'));
      if (unsavedWishlist) {
        // update UI state
        dispatch({
          type: 'modules/arcade-root/SET_WISHLISTED_COUNTRIES',
          payload: {
            wishlistCountryCodes: unsavedWishlist.wishlistCountryCodes,
            previouslyWishlistedCountryCodes: unsavedWishlist.previouslyWishlistedCountryCodes,
          },
        });

        dispatch(apiSaveWishList(
          unsavedWishlist.wishlistCountryCodes,
          unsavedWishlist.previouslyWishlistedCountryCodes,
          unsavedWishlist.sourcePage,
          { collectionMethod: 'apiSaveWishList - Exception', moduleID: 'arcade-page-wrapper' }
        ));
      }
    }
  }, [isCountryListLoading, isCountryListApiError, isCountryListApiInitiated]);

  useEffect(() => {
    if (isWishlistSaving) {
      setWishlistSaveInitiated(true);
    }

    if (!isWishlistSaving && isWishlistSaveInitiated) {
      localStorage.removeItem('unsavedWishlist');
      setWishlistSaveInitiated(false);
    }
  }, [isWishlistSaving, isWishlistSaveInitiated]);

  useEffect(() => {
    const subPath = pathname.replace(BASE_UI_URL, '') || '/';
    const shouldRedirectToStamps = subPath === '/'
      || (subPath === '/landing' && hash !== '#faq')
      || subPath === '/enroll';
    let enrolledUserPath;
    const match = subPath.match(/^\/(.+)\/report$/);

    if (match && !isStampDetailsCalled) {
      enrolledUserPath = `${BASE_UI_URL}/${match[1]}`;
    } else {
      enrolledUserPath = shouldRedirectToStamps ? `${BASE_UI_URL}/stamps` : `${BASE_UI_URL}${subPath}${search}${hash}`;
    }

    if (userStatus === 'enrolled' && !canProfileRefresh && isMobile) {
      if (enrolledUserPath !== `${pathname}${search}${hash}`) {
        router.replace(enrolledUserPath);
      }
    }

    if (canProfileRefresh) {
      dispatch(apiProfileRefresh());
    }
  }, [userStatus, canProfileRefresh, isMobile, pathname]);

  useEffect(() => {
    if (userStatus && userStatus === 'enrolled') {
      dispatch(apiSurveyPromptStatus(setSurveyPromptStatus));
    }
  }, [userStatus]);

  const timeoutRef = React.useRef();

  useEffect(() => {
    const popUpIsAvailable = !isFullScreenError
      && !pathname.includes('unenroll')
      && !pathname.includes('enroll')
      && !pathname.includes('landing')
      && !pathname.includes('feedback');

    if (surveyPromptStatus && !isFirstTimeUser && popUpIsAvailable) {
      if (!timeoutRef.current) {
        timeoutRef.current = setTimeout(() => {
          setIsSurveyPopUpOpen(true);
          setSurveyPromptStatus(false);
        }, 30000);
      }
    } else if (surveyPromptStatus && !isFirstTimeUser && !popUpIsAvailable) {
      clearTimeout(timeoutRef.current);
      setSurveyPromptStatus(false);
    }
  }, [isFirstTimeUser, pathname, isFullScreenError, surveyPromptStatus]);

  useEffect(() => {
    if (isSurveyPopUpOpen) {
      dispatch(apiSubmitSurveyPrompt());
      setSurveyPromptStatus(false);
    }
  }, [isSurveyPopUpOpen]);

  const handleTryAgain = () => {
    track({
      event: {
        identifier: 'axp-passport-wishlist-save-error',
        element: `try-again:${sourcePage}`,
        type: 'click',
      },
    });
    dispatch(apiSaveWishList(
      wishlistCountryCodes.toJS(),
      previouslyWishlistedCountryCodes.toJS(),
      sourcePage,
      { collectionMethod: 'apiSaveWishList - Exception', moduleID: 'arcade-page-wrapper' }
    ));
  };

  const handleDismissBanner = () => {
    track({
      event: {
        identifier: 'axp-passport-wishlist-save-error',
        element: `x-but:${sourcePage}`,
        type: 'click',
      },
    });
    dispatch({
      type: 'modules/arcade-root/SET_WISHLIST_ERROR',
      payload: fromJS({
        isError: false,
        unsavedPayload: {
          wishlistCountryCodes,
          previouslyWishlistedCountryCodes,
          sourcePage,
        },
      }),
    });
  };

  useEffect(() => {
    if (isWishlistError) {
      track({
        event: {
          identifier: 'axp-passport-wishlist-save-error',
          element: `wishlist-error:${sourcePage}`,
          type: 'impression',
        },
      });
      setWishlistRetryCount((prevCount) => prevCount + 1);
    }
  }, [isWishlistError, sourcePage]);

  const refreshStampsList = () => {
    track({
      event: {
        identifier: 'axp-passport-stamp-load-error',
        element: 'refresh',
        type: 'click',
      },
    });
    dispatch(apiGetStamps({
      collectionMethod: 'apiGetStamps - Exception',
      moduleID: 'arcade-page-wrapper',
    }));
  };

  useEffect(() => {
    if (isStampsListApiError) {
      track({
        event: {
          identifier: 'axp-passport-stamp-home-error',
          element: 'load-error',
          type: 'impression',
        },
      });
      setStampsRetryCount((prevCount) => prevCount + 1);
    }
  }, [isStampsListApiError]);

  useEffect(() => {
    if (stampsScanStatus === STAMPS_SCAN_STATUS.IN_PROGRESS) {
      dispatch(apiPollStampsScanStatus());
    }
  }, [stampsScanStatus]);

  const handleRefresh = () => {
    track({
      event: {
        identifier: 'axp-passport-new-stamp-msg',
        element: 'refresh',
        type: 'click',
      },
    });
    router.replace(`${BASE_UI_URL}/stamps`);
    refreshStampsList();
  };

  const handleDismissScanBanner = () => {
    track({
      event: {
        identifier: 'axp-passport-fins-re-try',
        element: 'x-but',
        type: 'click',
      },
    });
    dispatch({
      type: 'modules/arcade-root/SET_SHOW_SCAN_BANNER',
      payload: false,
    });
  };

  const getRefreshBannerMessage = useCallback(() => {
    if (stampsScanStatus === STAMPS_SCAN_STATUS.IN_PROGRESS) {
      track({
        event: {
          identifier: 'axp-passport-feed-scan-msg',
          element: 'stamp-scan',
          type: 'impression',
        },
      });
      return intl.formatMessage({ id: 'stamp.banner.new' });
    }

    if (stampsScanStatus === STAMPS_SCAN_STATUS.COMPLETED) {
      track({
        event: {
          identifier: 'axp-passport-feed-new-stamp-msg',
          element: 'new-stamp-ref',
          type: 'impression',
        },
      });
      return (
        <FormattedMessage
          id="stamp.banner.refresh"
          values={{
            link: (chunks) => renderLink({ chunks, onClick: handleRefresh }),
          }}
        />
      );
    }

    track({
      event: {
        identifier: 'axp-passport-feed',
        element: 'fins-re-try',
        type: 'impression',
      },
    });
    return intl.formatMessage({ id: 'stamp.banner.error' });
  }, [stampsScanStatus]);

  // Prefetch images for share module used in overview and explore pages
  const prefetchImagesUrlArr = [
    `${gemUrl}/Images/shareBackground.png`,
    `${gemUrl}/Images/explore-layer.png`,
  ];

  useEffect(() => {
    if (userStatus && userStatus === 'enrolled' && !fetchedUrlModuleNames.includes(MODULE_NAME)) {
      setShouldPrefetch(true);
    }
  }, [userStatus, fetchedUrlModuleNames, gemUrl]);

  const prefetcher = shouldPrefetch && (
    <ImagePrefetcher
      imagesUrls={prefetchImagesUrlArr}
      onPrefetchExecuted={() => {
        dispatch({
          type: 'modules/arcade-root/PREFETCHED_MODULE_URLS',
          payload: MODULE_NAME,
        });
        setShouldPrefetch(false);
      }}
    />
  );

  const isBot = global.BROWSER && /bot|crawl|slurp|spider|mediapartners/i.test(navigator.userAgent);
  if ((!userStatus && !isBot) || canProfileRefresh) {
    return <LoadingPage />;
  }

  return (
    <main
      data-testid="arcade-main"
      className="height-full width-full"
    >
      {prefetcher}
      {showScanBanner && userStatus === 'enrolled'
        && (
          <NotificationBanner
            type="neutral"
            isNative={isNative}
            className={`${stampsScanStatus !== STAMPS_SCAN_STATUS.ERROR ? styles.hideDismissButton : ''}`}
            onDismiss={handleDismissScanBanner}
          >
            {getRefreshBannerMessage()}
          </NotificationBanner>
        )}
      {isStampsListApiError && userStatus === 'enrolled'
        && (
          <NotificationBanner
            isNative={isNative}
            type="warning"
            dismissable={false}
            className={`${styles.errorBanner} ${stampsRetryCount >= DEFAULT_MAX_RETRY ? '' : styles.hideDismissButton}`}
          >
            {stampsRetryCount >= DEFAULT_MAX_RETRY
              ? <FormattedMessage id="max.retry" />
              : (
                <FormattedMessage
                  id="stamp.banner.isStampsListApiError"
                  values={{
                    link: (chunks) => renderLink({
                      chunks,
                      onClick: refreshStampsList,
                    }),
                  }}
                />
              )}
          </NotificationBanner>
        )}
      {isWishlistError && userStatus === 'enrolled'
        && (
          <NotificationBanner
            isNative={isNative}
            type="warning"
            className={styles.errorBanner}
            onDismiss={handleDismissBanner}
          >
            {wishlistRetryCount >= DEFAULT_MAX_RETRY
              ? <FormattedMessage id="max.retry" />
              : (
                <FormattedMessage
                  id="wishlist.error"
                  values={{
                    link: (chunks) => renderLink({ chunks, onClick: handleTryAgain }),
                  }}
                />
              )}
          </NotificationBanner>
        )}
      {isSurveyPopUpOpen
        && (
          <SurveyPopUp
            setIsSurveyPopUpOpen={setIsSurveyPopUpOpen}
            router={router}
          />
        )}
      {children}
      {showNavbar && isMobile && userStatus !== 'ineligible'
        && (
          <NavBar
            pathname={pathname}
          />
        )}
    </main>
  );
};

RenderArcadeChildren.propTypes = {
  isMobile: PropTypes.bool,
  isNative: PropTypes.bool,
  children: PropTypes.element,
  router: PropTypes.shape({
    push: PropTypes.func,
    replace: PropTypes.func,
    location: PropTypes.PropTypes.shape({
      search: PropTypes.string,
      query: PropTypes.shape({
        faq: PropTypes.string,
      }),
      hash: PropTypes.string,
    }),
  }).isRequired,
  showNavbar: PropTypes.bool.isRequired,
  pathname: PropTypes.string.isRequired,
};

export default RenderArcadeChildren;
